import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const EMPTY_LINK = { label: "", url: "" };

export default function FooterAdmin() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/footer").then((r) => setForm(r.data)).catch(() => {}); }, []);

  if (!form) return <div className="font-body text-sm text-neutral-500" data-testid="footer-admin-loading">Loading…</div>;

  const onField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onLink = (idx, k) => (e) => {
    setForm((f) => {
      const arr = [...(f.social_links || [])];
      arr[idx] = { ...arr[idx], [k]: e.target.value };
      return { ...f, social_links: arr };
    });
  };

  const addLink = () => setForm((f) => ({ ...f, social_links: [...(f.social_links || []), EMPTY_LINK] }));
  const removeLink = (idx) => setForm((f) => ({ ...f, social_links: (f.social_links || []).filter((_, i) => i !== idx) }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/footer", {
        studio_name: form.studio_name, blurb: form.blurb,
        email: form.email, phone: form.phone, address: form.address,
        social_links: (form.social_links || []).filter((s) => s.label),
        tagline_right: form.tagline_right,
      });
      toast.success("Footer saved.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save.");
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-12 gap-10" data-testid="footer-admin">
      <section className="lg:col-span-7 bg-white border border-neutral-200 p-6 space-y-1">
        <div>
          <label className="font-body text-xs uppercase tracking-[0.25em] text-neutral-500">Studio name (big text)</label>
          <input value={form.studio_name || ""} onChange={onField("studio_name")} className="input-bare" data-testid="footer-studio-input" />
        </div>
        <div className="pt-4">
          <label className="font-body text-xs uppercase tracking-[0.25em] text-neutral-500">Blurb (under studio name)</label>
          <textarea value={form.blurb || ""} onChange={onField("blurb")} rows={3} className="input-bare resize-none" data-testid="footer-blurb-input" />
        </div>
        <div className="pt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6">
          <div>
            <label className="font-body text-xs uppercase tracking-[0.25em] text-neutral-500">Email</label>
            <input value={form.email || ""} onChange={onField("email")} className="input-bare" data-testid="footer-email-input" />
          </div>
          <div>
            <label className="font-body text-xs uppercase tracking-[0.25em] text-neutral-500">Phone</label>
            <input value={form.phone || ""} onChange={onField("phone")} className="input-bare" data-testid="footer-phone-input" />
          </div>
          <div className="pt-4 sm:col-span-2">
            <label className="font-body text-xs uppercase tracking-[0.25em] text-neutral-500">Address</label>
            <input value={form.address || ""} onChange={onField("address")} className="input-bare" data-testid="footer-address-input" />
          </div>
        </div>
        <div className="pt-4">
          <label className="font-body text-xs uppercase tracking-[0.25em] text-neutral-500">Tagline (right side of copyright)</label>
          <input value={form.tagline_right || ""} onChange={onField("tagline_right")} className="input-bare" data-testid="footer-tagline-input" />
        </div>
      </section>
      <aside className="lg:col-span-5 bg-white border border-neutral-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <p className="font-body text-xs uppercase tracking-[0.25em] text-neutral-500">Social links</p>
          <button type="button" onClick={addLink} className="font-body text-xs uppercase tracking-[0.2em] link-line" data-testid="footer-add-link-button">+ Add link</button>
        </div>
        <div className="space-y-2">
          {(form.social_links || []).map((s, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`footer-link-row-${i}`}>
              <input value={s.label} onChange={onLink(i, "label")} placeholder="Label" className="col-span-4 border border-neutral-200 px-3 py-2 font-body text-sm" data-testid={`footer-link-label-${i}`} />
              <input value={s.url} onChange={onLink(i, "url")} placeholder="URL" className="col-span-6 border border-neutral-200 px-3 py-2 font-body text-sm" data-testid={`footer-link-url-${i}`} />
              <button type="button" onClick={() => removeLink(i)} className="col-span-2 font-body text-xs uppercase tracking-[0.2em] text-[#FF3E00] link-line" data-testid={`footer-link-remove-${i}`}>Remove</button>
            </div>
          ))}
          {(!form.social_links || form.social_links.length === 0) && <p className="font-body text-sm text-neutral-500">No links yet.</p>}
        </div>
        <button type="submit" disabled={saving} className="mt-6 w-full bg-[#0A0A0A] text-[#F4F4F0] py-4 font-display font-bold uppercase tracking-widest text-sm hover:bg-[#FF3E00] transition-colors disabled:opacity-40" data-testid="footer-save-button">
          {saving ? "Saving…" : "Save Footer"}
        </button>
      </aside>
    </form>
  );
}
